import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DecimalPipe, NgClass } from '@angular/common';

@Component({
  selector: 'frmg-gradient-slider',
  templateUrl: './gradient-slider.component.html',
  styleUrls: ['./gradient-slider.component.scss'],
  imports: [
    DecimalPipe,
    NgClass
  ],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => GradientSliderComponent),
    multi: true
  }]
})

export class GradientSliderComponent implements ControlValueAccessor {
  @Input() min = 0;
  @Input() max = 100;
  @Input() invert = false;
  @Input() step = 1;
  @Input() trackGradient = 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 100%)';

  start = this.min;
  end = this.max;


  startColor = 'rgba(255,255,255,0)';
  endColor = 'rgba(255,255,255,0)';

  public onChange: (value: any) => void = () => {};
  public onTouched: () => void = () => {};
  public registerOnChange(fn: any): void { this.onChange = fn; }
  public registerOnTouched(fn: any): void { this.onTouched = fn; }
  public pct(v: number) { return ((v - this.min) / (this.max - this.min)) * 100; }
  get startPct() { return this.pct(this.start); }
  get endPct()   { return this.pct(this.end); }

  onStart(event: Event) {
    const minDelta = Math.floor((this.max - this.min) / 20);
    const v = (event.target as HTMLInputElement).valueAsNumber;
    if (v + minDelta > this.end && v + minDelta <= this.max)  {
      this.end = v + minDelta;
    } else if (v > this.end) this.end = v;
    this.start = v;
    this.updateThumbColors();
    this.emitChange();
  }

  onEnd(event: Event) {
    const minDelta = Math.floor((this.max - this.min) / 20);
    const v = (event.target as HTMLInputElement).valueAsNumber;
    if (v - minDelta < this.start && v - minDelta >= this.min)  {
      this.start = v - minDelta;
    } else if (v < this.start) this.start = v;
    this.end = v;
    this.updateThumbColors();
    this.emitChange();
  }

  emitChange() {
    this.onChange({ start: this.start, end: this.end, invert: this.invert });
  }

  public writeValue(value: { start: number; end: number, invert: boolean } | null): void {
    if (value) {
      this.start = value.start;
      this.end = value.end;
      this.invert = value.invert;
      this.updateThumbColors();
    }
  }

  ngOnInit() {
    this.updateThumbColors();
  }

  private updateThumbColors() {
    const stops = this.parseGradient(this.trackGradient);
    this.startColor = this.interpolateGradient(stops, this.startPct);
    this.endColor   = this.interpolateGradient(stops, this.endPct);
  }

  private parseGradient(gradient: string): { color: string; pos: number }[] {
    const stopRegex = /#([0-9a-f]{3,8})\b\s*(\d+%?)?/gi;
    const stops: { color: string; pos: number }[] = [];
    let match: RegExpExecArray | null;
    while ((match = stopRegex.exec(gradient)) !== null) {
      const color = `#${match[1]}`;
      const pos = match[2] ? parseFloat(match[2]) : NaN;
      stops.push({ color, pos });
    }
    if (stops.length === 1) stops.push({ color: stops[0].color, pos: 100 });
    stops.forEach((s, i) => { if (isNaN(s.pos)) s.pos = (i / (stops.length - 1)) * 100; });
    return stops.sort((a, b) => a.pos - b.pos);
  }

  private interpolateGradient(stops: { color: string; pos: number }[], pct: number) {
    const hexToRgb = (h: string) => {
      const n = h.length === 4 ? h.substring(1).split('').map(x => x + x).join('') : h.substring(1);
      const num = parseInt(n, 16);
      return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
    };

    const lower = stops.reduce((a, b) => (b.pos <= pct ? b : a), stops[0]);
    const upper = stops.find(s => s.pos >= pct) || stops[stops.length - 1];
    if (!upper || lower === upper) return lower.color;

    const t = (pct - lower.pos) / (upper.pos - lower.pos);
    const c1 = hexToRgb(lower.color), c2 = hexToRgb(upper.color);
    const r = Math.round(c1.r + t * (c2.r - c1.r));
    const g = Math.round(c1.g + t * (c2.g - c1.g));
    const b = Math.round(c1.b + t * (c2.b - c1.b));
    return `rgb(${r},${g},${b})`;
  }
}
